import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserId } from '@/store/selectors/userSelectors';
import { selectLocale } from '@/store/selectors/commonSelectors';
import {
  GIG_REGULATED_OPTIMOVE_TENANT_ID,
  GIG_UNREGULATED_OPTIMOVE_TENANT_ID,
  OPTIMOVE_TENANT_ID,
  OPTIMOVE_TOKEN,
} from '@/constants/constants';
import { showPushNotificationBonus } from '@/store/bonuses/actions';
import { isRegulatedMarket } from '@/utils/utils';

export const useWebNotifications = ({ enabled = true }) => {
  const userId = useSelector(selectUserId);
  const locale = useSelector(selectLocale);
  const dispatch = useDispatch();

  const [ready, setReady] = useState(false);
  const sdkRef = useRef<any | null>(null);

  const tenantId = isRegulatedMarket(locale)
    ? GIG_REGULATED_OPTIMOVE_TENANT_ID
    : GIG_UNREGULATED_OPTIMOVE_TENANT_ID;

  const loginReportedRef = useRef<string | null>(null);

  // wait for optimove sdk to be loaded
  const waitForOptimoveSDK = (timeoutMs = 8000, pollMs = 100): Promise<any> => {
    if (window.optimoveSDK?.API) {
      return Promise.resolve(window.optimoveSDK);
    }

    const scriptId = `ld-optimove-sdk-${OPTIMOVE_TENANT_ID}`;

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://sdk.optimove.net/v2/websdk/?tenant_id=${OPTIMOVE_TENANT_ID}&tenant_token=${OPTIMOVE_TOKEN}`;
      document.head.appendChild(script);
    }

    return new Promise((resolve, reject) => {
      const deadline = Date.now() + timeoutMs;
      const checkReady = () => {
        if (window.optimoveSDK?.API) return resolve(window.optimoveSDK);
        if (Date.now() > deadline) return reject(new Error('Optimove SDK failed to load in time'));
        setTimeout(checkReady, pollMs);
      };
      checkReady();
    });
  };

  // initialize service worker
  const ensureServiceWorker = (url = '/worker.js', scope = '/') => {
    if (!('serviceWorker' in navigator)) return Promise.resolve(null);
    navigator.serviceWorker.register(url, { scope }).catch(error => {
      console.error('Optimove: service worker', error);
    });
  };

  // initialize sdk and worker
  useEffect(() => {
    if (!enabled) return;
    Promise.all([waitForOptimoveSDK(), ensureServiceWorker('/worker.js', '/')])
      .then(([sdk]) => {
        sdkRef.current = sdk;

        //This SDK function will know to override Optimove Default pop-up and use this instead
        sdk.API.setPushReceivedListener((event: { data: { eventId: string | null } }) => {
          const bonusId = event.data.eventId;

          if (bonusId) {
            dispatch(showPushNotificationBonus(bonusId));
          }
        });

        try {
          // set page visit and trigger prompt
          sdk.API.setPageVisit(window.location.href, 'home');
        } catch {}
        setReady(true);
      })
      .catch(error => {
        // SDK never appeared — keep ready=false
        console.error('Optimove error:', error);
      });
  }, [enabled, dispatch]);

  // when worker is ready and user is logged in, trigger login only once
  useEffect(() => {
    if (!enabled) return;
    if (!ready || !sdkRef.current?.API || userId == null) return;
    const key = `${tenantId}_${userId}`;
    if (loginReportedRef.current === key) return;
    try {
      sdkRef.current.API.reportEvent('login', {}, null, key);
      loginReportedRef.current = key;
    } catch {}
  }, [enabled, ready, userId, tenantId]);

  return { ready, sdk: sdkRef.current };
};
